import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, Button, Alert, LinearProgress
} from '@mui/material';
import { useI18n } from '../../i18n/i18n';
import { analyzeIncidentPatterns } from './incidentsAnalytics';
import { useIncidentNotifications } from './incidentsNotifications';

export default function IncidentsPatternsAnalysis() {
  const { t } = useI18n();
  const { notifyRecurringPattern } = useIncidentNotifications();
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastAnalysis, setLastAnalysis] = useState(null);

  useEffect(() => {
    loadPatterns();
  }, []);

  const loadPatterns = async () => {
    try {
      setLoading(true);
      const analysis = await analyzeIncidentPatterns();
      setPatterns(analysis.recurringPatterns);
      setLastAnalysis(new Date());
      
      // Сповіщаємо про нові критичні патерни
      analysis.recurringPatterns
        .filter(pattern => pattern.severity === 'high')
        .forEach(pattern => {
          notifyRecurringPattern(pattern);
        });
    } catch (error) {
      console.error('Error loading patterns:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error'
    };
    return colors[severity] || 'default';
  };

  const getPatternTypeColor = (type) => {
    const colors = {
      client: 'primary',
      technical: 'secondary',
      logistics: 'info',
      safety: 'error'
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('common.loading')}</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">
          {t('incidents_recurring_patterns') || 'Recurring Patterns Analysis'}
        </Typography>
        <Button variant="outlined" onClick={loadPatterns}>
          {t('common.refresh') || 'Refresh'}
        </Button>
      </Stack>

      {lastAnalysis && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('common.last_updated') || 'Last updated'}: {lastAnalysis.toLocaleString()}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Статистика патернів */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Patterns
              </Typography>
              <Typography variant="h4">
                {patterns.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                High Severity
              </Typography>
              <Typography variant="h4" color="error">
                {patterns.filter(p => p.severity === 'high').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Medium Severity
              </Typography>
              <Typography variant="h4" color="warning.main">
                {patterns.filter(p => p.severity === 'medium').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Client Patterns
              </Typography>
              <Typography variant="h4" color="primary.main">
                {patterns.filter(p => p.type === 'client').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Детальна таблиця патернів */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Detailed Patterns Analysis
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Pattern Type</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Occurrences</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Time Period</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patterns.map((pattern, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip 
                          label={pattern.type}
                          color={getPatternTypeColor(pattern.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {pattern.client}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" color="primary">
                          {pattern.count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={pattern.severity}
                          color={getSeverityColor(pattern.severity)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 days
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="outlined">
                            Analyze
                          </Button>
                          <Button size="small" variant="outlined">
                            View Incidents
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}